import { FC, useEffect, useMemo, useState } from 'react';
import {
  AlertBox,
  Button,
  Input,
  InputCheckbox,
  InputGroup,
  ModalInfo,
  ModalInfoProps,
} from '@/components';
import { useTranslation } from 'react-i18next';
import { Validation } from '@/models';
import { useInputBool, useInputText } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { profileSelectors } from '@/redux/selectors';
import parse from 'html-react-parser';
import { UserPreferencesRequest, amlbotAPI } from '@/api';
import { toast } from 'react-toastify';
import { COMMON_CONTAINER_ID } from '@/config';
import { profileActions } from '@/redux/actions';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import styles from './MonitoringSettingsModal.module.sass';

interface MonitoringSettingsModalProps extends ModalInfoProps {}

const MonitoringSettingsModal: FC<MonitoringSettingsModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const [finished, setFinished] = useState(false);
  const dispatch = useAppDispatch();
  const {
    data: profileData,
    loading,
    loadingPreferences,
    error,
  } = useAppSelector(profileSelectors.getProfileSlice);
  const monitoringBonusCount = useAppSelector(
    profileSelectors.monitoringBonusCount,
  );
  const hasApiAccess = useAppSelector(profileSelectors.hasApiAccess);

  const CHECKBOX_VALIDATIONS: Validation[] = useMemo(() => [], []);
  const THRESHOLD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'gt',
        value: 10,
        message: t('translation.field_must_be_greater', { value: 10 }),
      },
      {
        name: 'lt',
        value: 100,
        message: t('translation.field_must_be_less', { value: 100 }),
      },
      {
        name: 'regex',
        value: /^\d+$/i,
        message: t('translation.field_must_contain_only_digits'),
      },
    ],
    [t],
  );

  const FREQUENCY_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'gt',
        value: 0,
        message: t('translation.field_must_be_greater', { value: 0 }),
      },
      {
        name: 'lt',
        value: 180,
        message: t('translation.field_must_be_less', { value: 180 }),
      },
      {
        name: 'regex',
        value: /^\d+$/i,
        message: t('translation.field_must_contain_only_digits'),
      },
    ],
    [t],
  );

  const WEBHOOK_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'regex',
        value:
          /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
        message: t('translation.field_is_not_url'),
      },
    ],
    [t],
  );

  const autoAddField = useInputBool(
    profileData?.preferences?.monitoring?.auto,
    CHECKBOX_VALIDATIONS,
  );
  const sendEmailField = useInputBool(
    profileData?.preferences?.monitoring?.send_email,
    CHECKBOX_VALIDATIONS,
  );
  const thresholdField = useInputText(
    profileData?.preferences?.monitoring?.threshold,
    THRESHOLD_VALIDATIONS,
  );
  const frequencyField = useInputText(
    profileData?.preferences?.monitoring?.frequency,
    FREQUENCY_VALIDATIONS,
  );
  const webHookField = useInputText(
    profileData?.webhookUrl || '',
    WEBHOOK_VALIDATIONS,
  );

  useEffect(() => {
    if (finished === loadingPreferences) return;

    if (finished && !error && finished !== loadingPreferences) {
      onClose();
    }

    setFinished(loadingPreferences);
  }, [loadingPreferences, error]);

  const submitHandler = () => {
    if (
      !loading &&
      !loadingPreferences &&
      thresholdField.isValid &&
      frequencyField.isValid &&
      webHookField
    ) {
      const payload: UserPreferencesRequest = {
        preferences: {
          monitoring: {
            auto: autoAddField.value,
            send_email: sendEmailField.value,
            threshold: Number(thresholdField.value),
            frequency: Number(frequencyField.value),
          },
        },
        url: webHookField.value,
      };
      dispatch(profileActions.updatePreferences({ payload }));
    }
  };

  const cancelHandler = () => {
    onClose();
    if (!!error) {
      dispatch(profileActions.setError(null));
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.monitoring_preference_modal')}
      className={styles.modal}
      actions={
        <>
          <Button
            type="primary"
            disabled={loading || loadingPreferences}
            isLoading={loadingPreferences}
            corner="rect"
            onClick={submitHandler}
          >
            {t('translation.save_changes_btn')}
          </Button>
          <Button
            type="secondary"
            disabled={loading || loadingPreferences}
            corner="rect"
            onClick={cancelHandler}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <div className={styles.form}>
        {!!monitoringBonusCount && (
          <InputCheckbox
            id="autoAdd"
            name="autoAdd"
            value={autoAddField.value as boolean}
            onChange={autoAddField.onChange}
            required
          >
            <span className={styles.label}>
              {parse(t('translation.preference_monitoring_auto_add'))}
            </span>
          </InputCheckbox>
        )}
        {hasApiAccess && !!profileData?.webhookUrl && (
          <InputCheckbox
            id="sendEmail"
            name="sendEmail"
            value={sendEmailField.value as boolean}
            onChange={sendEmailField.onChange}
            required
          >
            <span className={styles.label}>
              {parse(t('translation.preference_monitoring_send_email'))}
              &nbsp;
              <Popover
                zIndex={9999}
                content={parse(
                  t('translation.monitoring_send_email_description'),
                )}
              >
                <ExclamationCircleOutlined className={styles.iconButton} />
              </Popover>
            </span>
          </InputCheckbox>
        )}

        <InputGroup
          label={t('translation.preference_monitoring_risk_threshold')}
          className={styles.group}
        >
          <Input
            id="threshold"
            name="threshold"
            type="text"
            value={thresholdField.value as string}
            onChange={thresholdField.onChange}
            onBlur={thresholdField.onBlur}
            disabled={loading}
            isError={thresholdField.isDirty && !thresholdField.isValid}
            required
          />
          {thresholdField.isDirty && !thresholdField.isValid && (
            <AlertBox type="danger" small>
              {{ description: thresholdField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>
        <InputGroup
          label={t('translation.preference_monitoring_frequency')}
          className={styles.group}
        >
          <Input
            id="frequency"
            name="frequency"
            type="text"
            value={frequencyField.value as string}
            onChange={frequencyField.onChange}
            onBlur={frequencyField.onBlur}
            disabled={loading}
            isError={frequencyField.isDirty && !frequencyField.isValid}
            required
          />
          {frequencyField.isDirty && !frequencyField.isValid && (
            <AlertBox type="danger" small>
              {{ description: frequencyField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>
        {hasApiAccess && (
          <InputGroup
            label={t('translation.monitoring_webhook_field')}
            className={styles.group}
          >
            <Input
              id="webhook"
              name="webhook"
              type="text"
              value={webHookField.value as string}
              onChange={webHookField.onChange}
              onBlur={webHookField.onBlur}
              disabled={loading}
              isError={webHookField.isDirty && !webHookField.isValid}
            />
            {webHookField.isDirty && !webHookField.isValid && (
              <AlertBox type="danger" small>
                {{ description: webHookField.errors[0] }}
              </AlertBox>
            )}
          </InputGroup>
        )}
      </div>
    </ModalInfo>
  );
};

export default MonitoringSettingsModal;
